import React from 'react';
import { formatTime } from '../utils/contentUtils';
import { resolveAudioLink } from '../utils/audioLink';
import MediaLinkInput from './MediaLinkInput';

/**
 * "Use a link instead of a copy" for an audio clip.
 *
 * The picture version of this bargain is a good one; the audio version is a
 * better one. Uploading a two-minute recording writes ~1.2 MB of base64 into
 * the deck, into every autosave of it, and into every export of it — for a file
 * the author already has hosted somewhere. Linking stores the address.
 *
 * Nothing is claimed about a link until the browser has actually played it far
 * enough to read the header (see resolveAudioLink), so a 404, a typo'd path and
 * a page-that-is-not-a-file are all reported as themselves rather than accepted
 * and discovered later on a projector.
 */
const describeSuccess = (result) => {
  const length = result.duration ? ` (${formatTime(result.duration)})` : '';
  return `Linked and playing${length}. The deck stores the address, not the file.`;
};

// The waveform is the one thing a link cannot always have: drawing it means
// reading the bytes, which needs the host's CORS permission, while playing the
// clip does not. That is a difference in decoration, not in function — so it is
// said once here rather than being allowed to look like a broken clip later.
const describeWarning = () =>
  'If the timeline shows as a plain bar rather than a waveform, the host is serving the clip but not letting this app read it. Allowing this site in the bucket’s CORS rules draws the real waveform; playing and scrubbing work either way.';

const AudioLinkInput = ({
  onUseLink,
  label = 'Or use a linked clip',
  description = 'Cloudflare R2, an LMS folder, any CDN. Paste the address of the .mp3, .m4a or .wav itself — the clip stays where it is hosted, so the deck stays small.',
  placeholder = 'https://…/clip.mp3',
  buttonLabel = 'Use link',
  tone = 'light',
  disabled = false
}) => (
  <MediaLinkInput
    resolve={resolveAudioLink}
    onResolved={(result) => onUseLink?.(result.url, result)}
    describeSuccess={describeSuccess}
    describeWarning={describeWarning}
    label={label}
    description={description}
    placeholder={placeholder}
    buttonLabel={buttonLabel}
    checkingLabel="Playing it…"
    tone={tone}
    disabled={disabled}
  />
);

export default AudioLinkInput;
